import { useEffect, useState, useRef } from "react";
import type { Model } from "survey-core";
import {
  readSessionDraft,
  mergeSessionDraft,
  SessionDraftPayload,
  chooseLatest,
  buildDraftKey,
  getDraftUserId,
} from "../helpers/SessionStorageHelpers";
import { TokenPrefillPayload } from "../hooks/useTokenPrefillHook";
import { getFlattenedData } from "../helpers/SurveyJSHelpers";
import { useUserAuth } from "../context/AuthContext";
import { IClientData } from "../models/ClientData";

export type UseSurveyRestoreHookOptions = {
  storageKey: string;
  initialPayload?: IClientData;
  apiUpdatedAt?: string;
  tokenPrefill?: TokenPrefillPayload;
};

type RestoreSource = "session" | "initial" | "token";

export function useSurveyRestoreHook(model: Model, opts: UseSurveyRestoreHookOptions) {
  const {
    storageKey,
    initialPayload,
    tokenPrefill,
  } = opts;

  const [restored, setRestored] = useState(false);
  const { user } = useUserAuth();
  const currentUserId = getDraftUserId(user);
  const lastRestoredKeyRef = useRef<string | null>(null);
  const lastTokenPrefillRef = useRef<TokenPrefillPayload | undefined>(undefined);

  useEffect(() => {
    // Allow re-running if token prefill becomes available (was undefined, now has data)
    const tokenResolved = !lastTokenPrefillRef.current?.data && tokenPrefill?.data;

    // Prevent re-running for the same storage key unless token just resolved
    if (lastRestoredKeyRef.current === storageKey && !tokenResolved) {
      return;
    }

    // if user logs in off the back of a form (lander) do not restore the data
    if (lastRestoredKeyRef.current === buildDraftKey("anon") && currentUserId !== "anon") return;

    // Security check: if logged in, storage key must match current user's key
    if (storageKey !== buildDraftKey(currentUserId)) {
      return;
    }

    const sessionPayload = readSessionDraft(storageKey);
    const apiData = initialPayload
      ? getFlattenedData(initialPayload ?? {})
      : undefined;
    const initialPayloadNormalized = apiData
      ? {
        data: apiData,
        updatedAt: initialPayload?.lastUpdated ||
          new Date(0).toISOString(),
      }
      : null;

    let winner: SessionDraftPayload | null = null;
    let winnerSource: RestoreSource | null = null;

    // Merge token prefill with initialData (token fields override initialData)
    if (tokenPrefill?.data) {
      const flattenedTokenData = getFlattenedData(tokenPrefill.data);
      const baseData = initialPayloadNormalized?.data || {};

      const mergedData = {
        ...baseData,
        ...flattenedTokenData,
      };

      winner = {
        data: mergedData,
        updatedAt: tokenPrefill.updatedAt,
      };
      winnerSource = "token";
    } else {
      winner = chooseLatest(sessionPayload, initialPayloadNormalized);
      if (winner === sessionPayload && sessionPayload) {
        winnerSource = "session";
        // The draft only holds the fields each form wrote, so it is layered over the API data rather than replacing it;
        // landers' own progress ("lander:<slug>") is never form data.
        const draftFields = Object.fromEntries(
          Object.entries(sessionPayload.data ?? {}).filter(([field]) => !field.startsWith("lander:"))
        );
        winner = { ...sessionPayload, data: { ...(initialPayloadNormalized?.data ?? {}), ...draftFields } as IClientData };
      } else if (winner === initialPayloadNormalized && initialPayloadNormalized) {
        winnerSource = "initial";
      }
    }

    if (winner) {
      model.data = winner.data || {};
      model.getAllQuestions().forEach((q) => {
        const name = q.name;
        if (!name) return;
        const flattenedData = winner!.data as IClientData;
        if (Object.prototype.hasOwnProperty.call(flattenedData, name)) {
          q.value = flattenedData[name as keyof IClientData];
        }
      });
      model.render();

      if (winnerSource === "session" || winnerSource === "token") {
        const ownFields: Record<string, unknown> = {};
        model.getAllQuestions().forEach((question) => {
          if (question.name) ownFields[question.name] = model.data[question.name];
        });
        mergeSessionDraft(storageKey, ownFields, winner.updatedAt || new Date().toISOString());
      }
    }

    lastRestoredKeyRef.current = storageKey;
    lastTokenPrefillRef.current = tokenPrefill;
    setRestored(true);
  }, [storageKey, currentUserId, tokenPrefill, initialPayload, model]);

  return { restored };
}
